import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const SECTIONS = [
  {
    title: 'Trial & Grace',
    fields: [
      { key: 'trial_duration_days', label: 'Trial Duration (days)', type: 'number' },
      { key: 'grace_period_days', label: 'Grace Period (days)', type: 'number' },
    ],
  },
  {
    title: 'Discounts',
    fields: [
      { key: 'yearly_discount_percent', label: 'Yearly Discount (%)', type: 'number', step: '0.01' },
      { key: 'volume_discount_2_5', label: 'Volume Discount 2-5 Properties (%)', type: 'number', step: '0.01' },
      { key: 'volume_discount_6_10', label: 'Volume Discount 6-10 Properties (%)', type: 'number', step: '0.01' },
      { key: 'volume_discount_11_plus', label: 'Volume Discount 11+ Properties (%)', type: 'number', step: '0.01' },
    ],
  },
  {
    title: 'Tax',
    fields: [
      { key: 'gst_rate', label: 'GST Rate (%)', type: 'number', step: '0.01' },
    ],
  },
  {
    title: 'Invoice',
    fields: [
      { key: 'invoice_prefix', label: 'Invoice Prefix', type: 'text' },
    ],
  },
  {
    title: 'Razorpay',
    fields: [
      { key: 'razorpay_key_id', label: 'Razorpay Key ID', type: 'text' },
      { key: 'razorpay_key_secret', label: 'Razorpay Key Secret', type: 'password' },
    ],
  },
  {
    title: 'Dunning',
    fields: [
      { key: 'dunning_email_1_days', label: 'Dunning Email 1 (days before)', type: 'number' },
      { key: 'dunning_email_2_days', label: 'Dunning Email 2 (days before)', type: 'number' },
      { key: 'dunning_email_3_days', label: 'Dunning Email 3 (days before)', type: 'number' },
    ],
  },
  {
    title: 'Company Info',
    fields: [
      { key: 'company_name', label: 'Company Name', type: 'text' },
      { key: 'company_address', label: 'Company Address', type: 'text' },
      { key: 'company_gstin', label: 'GSTIN', type: 'text' },
      { key: 'company_pan', label: 'PAN', type: 'text' },
      { key: 'company_email', label: 'Email', type: 'text' },
      { key: 'company_phone', label: 'Phone', type: 'text' },
    ],
  },
];

function SubscriptionConfig({ token }) {
  const { isMobile } = useIsMobile();
  const headers = (extra = {}) => ({ Authorization: `Bearer ${token}`, ...extra });

  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/v1/subscription/admin/config', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load config');
      setConfig(data.config || data || {});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConfig(); }, [token]);

  const saveConfig = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const res = await fetch('/api/v1/subscription/admin/config', {
        method: 'PUT',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(config),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setSuccess('Configuration saved.');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const setVal = (key, val) => setConfig((c) => ({ ...c, [key]: val }));

  /* ── Styles ── */
  const inputStyle = {
    padding: '0.5rem 0.6rem', border: '1px solid var(--theme-border, #ccc)',
    borderRadius: 6, fontSize: '0.85rem', width: '100%', boxSizing: 'border-box',
  };
  const labelStyle = { fontWeight: 600, fontSize: '0.8rem', marginBottom: 2, display: 'block', color: '#34495e' };
  const btnPrimary = {
    padding: '0.55rem 1.4rem', background: 'var(--theme-primary, #2563eb)', color: '#fff',
    border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem',
  };
  const sectionHeaderStyle = {
    fontSize: '0.95rem', fontWeight: 700, color: 'var(--theme-primary-dark, #1e3a5f)',
    borderBottom: '2px solid var(--theme-border, #dcdde1)', paddingBottom: '0.4rem',
    marginBottom: '0.7rem', marginTop: '1.2rem',
  };

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.8rem' }}>
        <h2 style={{ color: 'var(--theme-primary-dark, #1e3a5f)', margin: 0 }}>Subscription Configuration</h2>
        <button style={btnPrimary} onClick={saveConfig} disabled={saving || loading}>
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner">{success}</div>}
      {loading && <div style={{ color: '#636e72', marginBottom: '0.8rem' }}>Loading configuration...</div>}

      {!loading && (
        <div className="card" style={{ padding: '1rem' }}>
          {SECTIONS.map((section) => (
            <div key={section.title}>
              <div style={sectionHeaderStyle}>{section.title}</div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '0.5rem' }}>
                {section.fields.map((f) => (
                  <div key={f.key}>
                    <label style={labelStyle}>{f.label}</label>
                    <input
                      style={inputStyle}
                      type={f.type || 'text'}
                      step={f.step || undefined}
                      min={f.type === 'number' ? '0' : undefined}
                      value={config[f.key] ?? ''}
                      onChange={(e) => setVal(f.key, f.type === 'number' ? e.target.value : e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SubscriptionConfig;
